import React, { useState, useEffect } from "react";
import {
	useParams,
	Link,
	useNavigate,
	useSearchParams,
} from "react-router-dom";
import {
	Form,
	Input,
	Select,
	Row,
	Col,
	Space,
	Modal,
	Card,
	Divider,
	Typography,
	InputNumber,
	Switch,
	Tooltip,
	TimePicker,
} from "antd";
import {
	InnerForm,
	ProductData,
	PublishSettings,
	Container,
	CardContainer,
	Section,
	AntdModal,
	InfoContainer,
	InnerBlocks,
	FilterContainer,
	FilterActions,
} from "./styles";
import LoadingSkeleton from "./components/loading-skeleton/LoadingSkeleton";
import { CloseCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useAuth } from "../../../../../contexts/authContext";
import {
	openNotificationWithIcon,
	TYPE,
} from "../../../../../utils/notificationToast";
import Main from "../../../../layout/main/Main";
import { COLORS, Typo } from "../../../../layout/theme";
import SignInTemplate from "../../../../layout/sign-in-template/SignInTemplate";
import {
	get_agent_config_by_id,
	update_agent_config,
} from "../../../../../api/endpoints/agents";
import moment from "moment";
import { CONSTANTS } from "../../../../../utils/constants";
import TextArea from "antd/lib/input/TextArea";
import PrimaryButton from "../../../../common/antd-buttons/PrimaryButton";
import DefaultButton from "../../../../common/antd-buttons/DefaultButton";
import AntdSelect from "../../../../common/antd-select/AntdSelect";

const { Option } = Select;
const GUTTER = 10;

export default function UpdateAgentConfig() {
	const { id } = useParams();
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");

	const [submitting, setSubmitting] = useState(false);
	const [initialValues, setInitialValues] = useState(null);

	const typeOfCommunicationValue = Form.useWatch("typeOfCommunication", form);
	const sellerProfileValue = Form.useWatch("sellerProfile", form);
	const agentOOSValue = Form.useWatch("agentOOS", form);

	useEffect(() => {
		const fetch_agent_config_by_id = async () => {
			const data = await get_agent_config_by_id(id);

			setInitialValues({
				laboratoryName: data.client.name,
				laboratoryId: data.clientId,
				name: data.name,
				language: data.language,
				clientName: data.clientName,
				typeOfCommunication: data.typeOfCommunication,
				sellerProfile: data.sellerProfile,
				supportEmail: data.supportEmail,
				activeOffers: data.activeOffers,
				activeSuggesteds: data.activeSuggesteds,
				activeFreeCommunication: data.activeFreeCommunication,
				state: data.deleted ? "DELETED" : "ACTIVE",
				autoMessageEveryDays: data.autoMessageEveryDays ?? null,
				autoMessageHour: data.autoMessageTime
					? moment(data.autoMessageTime, "HH:mm")
					: null,
				deriveToSupportOnOptOut: data.deriveToSupportOnOptOut,
			});

			form.setFieldsValue({
				...data,
				state: data.deleted ? "DELETED" : "ACTIVE",
				autoMessageEveryDays: data.autoMessageEveryDays ?? null,
				autoMessageHour: data.autoMessageTime
					? moment(data.autoMessageTime, "HH:mm")
					: null,
				agentOOS: !!data.agentOOS,
				agentOOSMessage: data.agentOOSMessage ?? "",
			});
		};

		if (id) {
			fetch_agent_config_by_id();
		}
	}, []);

	const onFinish = async (values) => {
		try {
			setSubmitting(true);

			const defaultDays = 7;
			const defaultHour = "09:00";
			const agentConfigPayload = {
				id,
				laboratoryId: initialValues.laboratoryId,
				name: values.name,
				language: values.language,
				clientName: values.clientName,
				typeOfCommunication: values.typeOfCommunication,
				sellerProfile: values.sellerProfile,
				supportEmail: values.supportEmail,
				state: values.state === "DELETED",
				activeOffers: !!values.activeOffers,
				activeSuggesteds: !!values.activeSuggesteds,
				activeFreeCommunication: true,
				autoMessageEveryDays: values.autoMessageEveryDays ?? defaultDays,
				autoMessageHour: values.autoMessageHour
					? values.autoMessageHour.format("HH:mm")
					: defaultHour,
				deriveToSupportOnOptOut: !!values.deriveToSupportOnOptOut,
				agentOOS: !!values.agentOOS,
				agentOOSMessage: values.agentOOSMessage ?? "",
			};

			const payload = {
				id,
				agentConfig: agentConfigPayload,
			};

			await update_agent_config(id, payload);

			openNotificationWithIcon(
				TYPE.SUCCESS,
				"Configuracion actualizada con éxito",
				"La configuracion del agente se actualizó con éxito",
			);
			navigate(`/admin/laboratoryAgentConfigs?uri=${uri}`);
		} catch (error) {
			console.log(error);
			openNotificationWithIcon(
				TYPE.ERROR,
				"Error al actualizar lo datos",
				"Un error ocurrio al intentar actualizar datos, contacte con soporte a soporte@checkpos.com.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	if (!id) navigate("/admin/laboratoryAgentConfigs?uri=" + uri);

	const handleBack = () => {
		Modal.confirm({
			title: "¿Seguro que quiere volver sin guardar?",
			icon: <CloseCircleOutlined style={{ color: "red" }} />,
			content:
				"Si regresa sin guardar se perderán los datos que hayan sido cargados",
			okText: "Sí",
			okType: "danger",
			cancelText: "No",
			onOk: () => {
				navigate("/admin/laboratoryAgentConfigs?uri=" + uri);
			},
		});
	};

	const getTypeOfCommunicationDescription = (value) => {
		switch (value) {
			case "amable":
				return "Tu estilo de comunicación es amable y cercano: saludas cordialmente, agradeces y mantienes un tono respetuoso y positivo.";
			case "conciso_directo":
				return "Tu estilo de comunicación es conciso y directo: vas al punto y respondes en pocas frases.";
			case "didactico":
				return "Tu estilo de comunicación es didáctico: explicas los pasos y recomendaciones de forma clara y ordenada.";
			case "formal":
				return "Tu estilo de comunicación es formal y corporativo: usas un tono respetuoso, profesional, sin modismos ni emojis.";
			case "informal_cercano":
				return "Tu estilo de comunicación es informal y cercano: puedes usar un tono de conversación cotidiano, algún emoji moderado cuando sea apropiado, pero sin perder profesionalismo ni claridad.";
			case "entusiasta":
				return "Tu estilo de comunicación es entusiasta: celebras los avances del pedido, reconoces cuando el cliente toma decisiones inteligentes para su negocio y mantienes un tono motivador sin exagerar.";
			case "analitico":
				return "Tu estilo de comunicación es analítico: cuando corresponde, destacas números clave (descuentos, cantidades sugeridas, comparaciones simples) de forma clara, resaltando el impacto económico para la farmacia.";
			default:
				return "Tu estilo de comunicación es amable y claro.";
		}
	};

	const getSellerProfileDescription = (value) => {
		switch (value) {
			case "consultivo":
				return "Tu perfil de vendedor es consultivo, priorizando las necesidades reales del cliente a partir del pedido sugerido.";
			case "upselling_suave":
				return "Tu perfil de vendedor es orientado a ventas suave: cuando detectas un interés de compra, propones formatos más grandes siempre como sugerencia opcional sin presionar.";
			case "cross_selling":
				return "Tu perfil de vendedor es orientado a ventas cruzadas: propones productos complementarios lógicos basándote en el nombre.";
			case "ofertas_volumen":
				return "Tu perfil de vendedor es orientado a ofertas y volumen: destacas siempre las ofertas vigentes, escalas por volumen y oportunidades proponiendo cantidades que aprovechen mejor el descuento.";
			default:
				return "Tu perfil de vendedor es consultivo, priorizando las necesidades reales del cliente a partir del pedido sugerido.";
		}
	};

	return (
		<Main>
			<Main.Header>
				<Main.Breadcrumb>
					<Main.BreadcrumbItem>
						{uri ? (
							<Link to={"/" + uri}>Configuración General</Link>
						) : (
							<Link to="/">Home</Link>
						)}
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.BreadcrumbItem>
						<Link to={"/admin/laboratoryAgentConfigs?uri=" + uri}>
							Configuracion de agente
						</Link>
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.ActiveBreadcrumbItem>
						Editar configuracion
					</Main.ActiveBreadcrumbItem>
				</Main.Breadcrumb>
			</Main.Header>

			<Main.Body>
				{!initialValues ? (
					<LoadingSkeleton />
				) : (
					<Form
						form={form}
						name="agent-config"
						onFinish={onFinish}
						autoComplete="off"
						requiredMark={false}
						layout="vertical"
						initialValues={initialValues}
					>
						<InnerBlocks>
							<FilterContainer>
								<Form.Item
									label={
										<label style={{ color: COLORS.Primary, fontWeight: 600 }}>
											Laboratorio
										</label>
									}
									name="laboratoryName"
								>
									<Input
										placeholder="Nombre"
										disabled
										style={{
											borderRadius: 15,
											height: 32,
											width: 300,
											color: COLORS.MediumGrey,
										}}
									/>
								</Form.Item>
								<FilterActions>
									<Form.Item style={{ marginBottom: 0 }}>
										<PrimaryButton
											type="Primary"
											color="white"
											bg="Primary"
											htmlType="submit"
											loading={submitting}
											block
											style={{ height: 32, fontWeight: 400 }}
										>
											Guardar Cambios
										</PrimaryButton>
									</Form.Item>
									<DefaultButton
										onClick={handleBack}
										loading={submitting}
										block
										style={{
											height: 32,
											fontWeight: 400,
											border: `1px solid ${COLORS.MediumGrey}`,
										}}
									>
										Volver sin guardar
									</DefaultButton>
								</FilterActions>
							</FilterContainer>

							<CardContainer>
								<Card
									title={<Typo level={5}>Configuración</Typo>}
									bodyStyle={{ paddingTop: 16 }}
								>
									<Row gutter={GUTTER}>
										{/* Columna 1: Datos */}
										<Col xs={24} md={12} lg={6}>
											<Card
												size="small"
												type="inner"
												title="Identidad"
												bordered={true}
											>
												<Form.Item
													label={
														<Typo level={16} style={{ whiteSpace: "nowrap" }}>
															Nombre
														</Typo>
													}
													name="name"
													rules={[{}]}
												>
													<Input style={{ width: "100%", borderRadius: 12 }} />
												</Form.Item>
												<Form.Item
													label={
														<Typo level={16} style={{ whiteSpace: "nowrap" }}>
															Empresa
														</Typo>
													}
													name="clientName"
													rules={[{}]}
												>
													<Input style={{ width: "100%", borderRadius: 12 }} />
												</Form.Item>
												<Form.Item
													label={
														<Space align="center">
															<Typo level={16} style={{ whiteSpace: "nowrap" }}>
																Tipo de comunicacion
															</Typo>
															<Tooltip
																title={getTypeOfCommunicationDescription(
																	typeOfCommunicationValue,
																)}
																placement="top"
															>
																<InfoCircleOutlined
																	style={{ color: COLORS.Primary }}
																/>
															</Tooltip>
														</Space>
													}
													name="typeOfCommunication"
													rules={[{}]}
												>
													<AntdSelect
														showSearch
														placeholder="Elegi un tipo"
														optionFilterProp="children"
														filterOption={(input, option) =>
															(option?.label ?? "")
																.toLowerCase()
																.includes(input.toLowerCase())
														}
														options={
															CONSTANTS.AGENT_SELLER_TYPE_OF_COMMUNICATION
														}
													/>
												</Form.Item>
												<Form.Item
													label={
														<Space align="center">
															<Typo level={16} style={{ whiteSpace: "nowrap" }}>
																Perfil de vendedor
															</Typo>
															<Tooltip
																title={getSellerProfileDescription(
																	sellerProfileValue,
																)}
																placement="top"
															>
																<InfoCircleOutlined
																	style={{ color: COLORS.Primary }}
																/>
															</Tooltip>
														</Space>
													}
													name="sellerProfile"
													rules={[{}]}
												>
													<AntdSelect
														showSearch
														placeholder="Elegi un tipo"
														optionFilterProp="children"
														filterOption={(input, option) =>
															(option?.label ?? "")
																.toLowerCase()
																.includes(input.toLowerCase())
														}
														options={CONSTANTS.AGENT_SELLER_PROFILES}
													/>
												</Form.Item>
											</Card>
										</Col>

										{/* Columna 2: Filtrado y opciones */}
										<Col xs={24} md={12} lg={6}>
											<Card
												size="small"
												type="inner"
												title="Opciones"
												bordered={true}
											>
												<Form.Item
													label="Ofrecer ofertas"
													name="activeOffers"
													valuePropName="checked"
												>
													<Switch />
												</Form.Item>
												<Form.Item
													label="Ofrecer sugeridos"
													name="activeSuggesteds"
													valuePropName="checked"
												>
													<Switch />
												</Form.Item>
												<Form.Item
													label={
														<Typo level={16} style={{ whiteSpace: "nowrap" }}>
															Idioma
														</Typo>
													}
													name="language"
													rules={[{}]}
												>
													<Input
														style={{ width: "100%", borderRadius: 12 }}
														disabled
													/>
												</Form.Item>
												<Form.Item
													label="Estado configuracion"
													name="state"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
													]}
												>
													<AntdSelect allowClear placeholder="Estado">
														<Option value="ACTIVE">Activa</Option>
														<Option value="DELETED">Inactiva</Option>
													</AntdSelect>
												</Form.Item>
											</Card>
										</Col>

										{/* Columna 3: Mensajería automática */}
										<Col xs={24} md={12} lg={6}>
											<Card
												size="small"
												type="inner"
												title="Mensajería automática"
												bordered
											>
												<Form.Item
													label="Cada cuántos días"
													name="autoMessageEveryDays"
													tooltip="Intervalo entre mensajes automáticos"
													rules={[
														({ getFieldValue }) => ({
															validator(_, v) {
																if (v == null || v === "")
																	return Promise.resolve();
																if (Number.isInteger(v) && v >= 1 && v <= 365)
																	return Promise.resolve();
																return Promise.reject(
																	"Ingrese un entero entre 1 y 365",
																);
															},
														}),
													]}
												>
													<InputNumber
														min={1}
														max={365}
														style={{ width: "100%", borderRadius: 12 }}
														placeholder="p. ej. 7"
													/>
												</Form.Item>

												<Form.Item
													label="Hora de envío"
													name="autoMessageHour"
													tooltip="Hora local del cliente (HH:mm)"
												>
													<TimePicker
														format="HH:mm"
														style={{ width: "100%", borderRadius: 12 }}
													/>
												</Form.Item>

												<Form.Item
													label="Correo de Soporte"
													name="supportEmail"
													tooltip="Correo para derivacion a soporte"
												>
													<Input style={{ width: "100%", borderRadius: 12 }} />
												</Form.Item>

												<Form.Item
													label="Aviso de bajas"
													name="deriveToSupportOnOptOut"
													valuePropName="checked"
												>
													<Switch />
												</Form.Item>
											</Card>
										</Col>
										{/* Columna 4: Fuera de servicio */}
										<Col xs={24} md={12} lg={6}>
											<Card
												size="small"
												type="inner"
												title="Fuera de servicio"
												bordered
											>
												<Form.Item
													label={
														<Space align="center">
															<Typo level={16} style={{ whiteSpace: "nowrap" }}>
																Activar OOS
															</Typo>
															<Tooltip
																title="Si está activo, el agente no procesará mensajes y responderá con un aviso de fuera de servicio."
																placement="top"
															>
																<InfoCircleOutlined
																	style={{ color: COLORS.Primary }}
																/>
															</Tooltip>
														</Space>
													}
													name="agentOOS"
													valuePropName="checked"
												>
													<Switch />
												</Form.Item>

												<Form.Item
													label="Mensaje OOS"
													name="agentOOSMessage"
													tooltip="Mensaje que se enviará al usuario cuando el agente esté fuera de servicio."
													dependencies={["agentOOS"]}
													rules={[
														({ getFieldValue }) => ({
															validator(_, v) {
																if (!getFieldValue("agentOOS"))
																	return Promise.resolve();
																if (v && v.trim().length >= 5)
																	return Promise.resolve();
																return Promise.reject(
																	"Ingresá un mensaje (mínimo 5 caracteres).",
																);
															},
														}),
													]}
												>
													<TextArea
														rows={5}
														placeholder="El agente está temporalmente fuera de servicio por mantenimiento. Intentá mas tarde."
														maxLength={300}
														showCount
														disabled={!agentOOSValue}
													/>
												</Form.Item>
											</Card>
										</Col>
									</Row>
								</Card>
							</CardContainer>
						</InnerBlocks>
					</Form>
				)}
			</Main.Body>
		</Main>
	);
}
